// src/ui/screens/HomeScreen.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { RequireAuth } from '../../components/RequireAuth';
import { BottomNavBar } from '../widgets/BottomNavBar';
import { DesktopNavRail } from '../widgets/DesktopNavRail';
import { UseAppBanner } from '../../components/UseAppBanner';
import { ConversationsScreen } from './chats/ConversationsScreen';
import { PostScreen } from './post/PostScreen';
import { SettingsScreen } from './settings/SettingsScreen';

// ── Top-level tab <-> URL mapping ───────────────────────────────────────────
//
// FIX: `currentIndex` used to be plain `useState(1)`, so it reset to Home
// on every remount — including the remount that happens when the browser's
// back button returns here from a page navigated to from Settings or
// Chats (e.g. `/settings/about`, `/chat/:id`). That made back "throw you to
// Home" even though nothing about the back button itself was broken; this
// page had just forgotten which top-level tab it was showing. Mapping the
// tab to a `?view=` query param means the URL remembers it, so browser
// history (and the back button) restores it correctly — the same fix
// already applied to PostScreen's own Posted/Atoms/Filatom tab via `?tab=`.
// The two params are independent and coexist fine on the same `/` URL,
// e.g. `/?view=home&tab=posted`.
const VIEW_PARAM_TO_INDEX = { chats: 0, home: 1, settings: 2 };
const VIEW_INDEX_TO_PARAM = ['chats', 'home', 'settings'];

// ── HomeScreen ──────────────────────────────────────────────────────────────
//
// DESKTOP LAYOUT NOTE: mobile keeps its original bottom nav bar completely
// unchanged (`BottomNavBar` is untouched — it's just wrapped in a `lg:hidden`
// div here). On large screens (lg: 1024px+) we swap it for a fixed left-hand
// nav rail instead (`DesktopNavRail`), the way a desktop app puts primary
// navigation in a vertical strip on the left rather than a bottom bar. The
// screen-content container gets a matching `lg:pl-[76px]` so nothing renders
// underneath the rail — this is a no-op below `lg`. PostScreen separately
// reserves its own additional space on desktop for a permanent category
// sidebar (see PostScreen.jsx); this file only owns the rail, since the
// Chats and Settings tabs need it too.

export const HomeScreen = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Lazy initializer: reads `?view=` once at mount — this is what makes the
  // browser back button land on the right top-level tab. See the
  // VIEW_PARAM_TO_INDEX comment above for the full story.
  const [currentIndex, setCurrentIndexState] = useState(() => {
    const param = searchParams.get('view');
    return VIEW_PARAM_TO_INDEX[param] ?? 1;
  });

  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  // Drop-in replacement for the old `setCurrentIndex` — same call
  // signature (accepts either a number or an updater function, exactly
  // like `useState`'s setter), so every existing call site keeps working
  // unchanged. The only difference is it also mirrors the new value into
  // the URL, using `replace` so tapping between tabs doesn't spam browser
  // history with an entry per tap.
  const setCurrentIndex = useCallback((indexOrUpdater) => {
    setCurrentIndexState((prev) => {
      const next = typeof indexOrUpdater === 'function' ? indexOrUpdater(prev) : indexOrUpdater;
      if (next !== prev) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('view', VIEW_INDEX_TO_PARAM[next] || 'home');
        setSearchParams(nextParams, { replace: true });
      }
      return next;
    });
  }, [searchParams, setSearchParams]);

  // ── Real viewport height tracking ─────────────────────────────────────────
  // Mobile browsers (Chrome/Samsung Internet included) compute `100vh` as the
  // LARGEST possible viewport — i.e. as if the address bar were hidden — even
  // while the address bar is actually showing and eating real screen space.
  // With `overflow: hidden` on this container, there's no way to scroll to
  // reveal the difference, so anything pinned to the bottom (our nav bar)
  // ends up rendered below the visible fold. `100dvh` (dynamic viewport
  // height) tracks the toolbar's current state and is the modern fix; the
  // `--app-vh` custom property below is a belt-and-suspenders fallback for
  // any older in-app WebViews that don't support `dvh` yet.
  useEffect(() => {
    const setAppHeight = () => {
      const height = window.visualViewport?.height || window.innerHeight;
      document.documentElement.style.setProperty('--app-vh', `${height}px`);
    };

    setAppHeight();

    window.addEventListener('resize', setAppHeight);
    window.addEventListener('orientationchange', setAppHeight);
    window.visualViewport?.addEventListener('resize', setAppHeight);

    return () => {
      window.removeEventListener('resize', setAppHeight);
      window.removeEventListener('orientationchange', setAppHeight);
      window.visualViewport?.removeEventListener('resize', setAppHeight);
    };
  }, []);

  const handleTabTap = (index) => {
    setCurrentIndex(index);
  };

  const updateUnreadCount = (count) => {
    setTotalUnreadCount(count);
  };

  const renderCurrentScreen = () => {
    switch (currentIndex) {
      case 0:
        // Conversations tab — auth required
        return (
          <RequireAuth>
            <ConversationsScreen
              onUnreadCountUpdated={updateUnreadCount}
              currentUserId={user?.uid}
            />
          </RequireAuth>
        );
      case 1:
        // Post/Home feed — stays public
        return <PostScreen />;
      case 2:
        // Settings tab — auth required
        return (
          <RequireAuth>
            <SettingsScreen />
          </RequireAuth>
        );
      default:
        return <PostScreen />;
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        // `var(--app-vh, 100dvh)` — prefer the JS-measured real viewport
        // height; if that variable somehow isn't set yet, fall back to the
        // browser's own dynamic-viewport unit rather than static 100vh.
        height: 'var(--app-vh, 100dvh)',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: '#08090B',
      }}
    >
      {/* Background layer */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: '#08090B',
          zIndex: 0,
        }}
      />

      {/* "Use App" promo banner — fixed to the very top, above everything,
          shown fresh every time the site loads. */}
      <UseAppBanner />

      {/* Screen content — Positioned.fill equivalent.
          zIndex explicitly lower than BottomNavBar/DesktopNavRail so nothing
          a child screen renders (e.g. its own internal fixed/absolute
          elements) can ever cover the nav and swallow taps.
          `lg:pl-[76px]` reserves space for the fixed desktop nav rail so
          content never renders underneath it; no-op on mobile. */}
      <div
        className="lg:pl-[76px]"
        style={{
          position: 'absolute',
          inset: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          zIndex: 1,
        }}
      >
        {renderCurrentScreen()}
      </div>

      {/* Mobile bottom nav — component itself is 100% untouched, just
          hidden on desktop via this wrapper. */}
      <div className="lg:hidden">
        <BottomNavBar
          currentIndex={currentIndex}
          onTap={handleTabTap}
          totalUnreadCount={totalUnreadCount}
        />
      </div>

      {/* Desktop left nav rail — replaces the bottom bar on large screens */}
      <DesktopNavRail
        currentIndex={currentIndex}
        onTap={handleTabTap}
        totalUnreadCount={totalUnreadCount}
      />
    </div>
  );
};

export default HomeScreen;